import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAcoes5W2H } from '@/hooks/useAcoes5W2H';
import { useUsers } from '@/hooks/useUsers';
import { PRIORIDADE_ACAO_LABELS } from '@/types/plano-acao-extended';

const acaoSchema = z.object({
  what_oque: z.string().min(1, 'O que fazer é obrigatório'),
  why_porque: z.string().min(1, 'Por que fazer é obrigatório'),
  who_quem: z.string().optional(),
  where_onde: z.string().optional(),
  when_quando_inicio: z.string().optional(),
  when_quando_fim: z.string().optional(),
  how_como: z.string().optional(),
  how_much_quanto: z.coerce.number().optional(),
  prioridade: z.string().optional(),
});

type AcaoFormData = z.infer<typeof acaoSchema>;

interface NewAcaoDialogProps {
  planoAcaoId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NewAcaoDialog = ({ planoAcaoId, open, onOpenChange }: NewAcaoDialogProps) => {
  const { createAcao, isCreating } = useAcoes5W2H(planoAcaoId);
  const { users } = useUsers();

  const form = useForm<AcaoFormData>({
    resolver: zodResolver(acaoSchema),
    defaultValues: {
      what_oque: '',
      why_porque: '',
      who_quem: '',
      where_onde: '',
      when_quando_inicio: '',
      when_quando_fim: '',
      how_como: '',
      how_much_quanto: undefined,
      prioridade: 'media',
    },
  });

  const onSubmit = (data: AcaoFormData) => {
    createAcao({
      plano_acao_id: planoAcaoId,
      what_oque: data.what_oque,
      why_porque: data.why_porque,
      who_quem: data.who_quem || null,
      where_onde: data.where_onde || null,
      when_quando_inicio: data.when_quando_inicio || null,
      when_quando_fim: data.when_quando_fim || null,
      how_como: data.how_como || null,
      how_much_quanto: data.how_much_quanto || null,
      prioridade: data.prioridade || null,
      status: 'pendente',
    }, {
      onSuccess: () => {
        form.reset();
        onOpenChange(false);
      },
    });
  };

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova Ação 5W2H</DialogTitle>
          <DialogDescription>
            Defina uma nova ação usando a metodologia 5W2H
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* WHAT - O que fazer */}
            <FormField
              control={form.control}
              name="what_oque"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded">WHAT</span>
                    O que fazer? *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Descreva a ação a ser realizada" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* WHY - Por que fazer */}
            <FormField
              control={form.control}
              name="why_porque"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded">WHY</span>
                    Por que fazer? *
                  </FormLabel>
                  <FormControl>
                    <Textarea placeholder="Justifique a necessidade desta ação" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              {/* WHO - Quem */}
              <FormField
                control={form.control}
                name="who_quem"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded">WHO</span>
                      Quem vai fazer?
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o responsável" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* WHERE - Onde */}
              <FormField
                control={form.control}
                name="where_onde"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded">WHERE</span>
                      Onde será feito?
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Local ou área" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* WHEN - Início */}
              <FormField
                control={form.control}
                name="when_quando_inicio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded">WHEN</span>
                      Data de início
                    </FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* WHEN - Fim */}
              <FormField
                control={form.control}
                name="when_quando_fim"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de conclusão</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* HOW - Como */}
            <FormField
              control={form.control}
              name="how_como"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded">HOW</span>
                    Como será feito?
                  </FormLabel>
                  <FormControl>
                    <Textarea placeholder="Descreva os passos ou método" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              {/* HOW MUCH - Custo */}
              <FormField
                control={form.control}
                name="how_much_quanto"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded">HOW MUCH</span>
                      Custo estimado (R$)
                    </FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" placeholder="0,00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Prioridade */}
              <FormField
                control={form.control}
                name="prioridade"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prioridade</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(PRIORIDADE_ACAO_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating ? 'Criando...' : 'Criar Ação'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default NewAcaoDialog;
